import { clone, isNotEmpty } from "@/fp";
import { computed, Ref, ref } from "@vue/composition-api";
import { ApiResult } from "../api/ApiResult.model";
import { ListResult } from "../models";

export interface CollectionApiInput {
  cursor?: string;
}

export interface UseCollectionOutput<T, A extends CollectionApiInput> {
  is_loading: Ref<boolean>;
  items: Ref<T[]>;
  clear: () => void;
  has_more: Readonly<Ref<Readonly<boolean>>>;
  has_no_results: Readonly<Ref<Readonly<boolean>>>;
  load_items: (a: A) => Promise<void>;
  load_more_items: () => Promise<void>;
  update_item: (by: string, item: T) => () => void;
  switch_source: (
    api_call: (a: A) => Promise<ApiResult<ListResult<T>>>
  ) => void;
  mutate: (fn: (a: T[]) => T[]) => void;
  update_items_by: (by: keyof T, updated_items: T[]) => void;
}

export const UseCollection = <T, A extends CollectionApiInput>(
  api_call: (a: A) => Promise<ApiResult<ListResult<T>>>
): UseCollectionOutput<T, A> => {
  let current_api_call: (a: A) => Promise<ApiResult<ListResult<T>>> = api_call;
  const is_loading = ref(false);
  let last_cursor = "";
  let last_payload: A | undefined;
  const items: Ref<T[]> = ref([]);

  const _has_no_results = ref(false);
  const has_no_results = computed(() => _has_no_results.value);

  const _has_more = ref(true);
  const has_more = computed(() => _has_more.value);

  const load_items = async (a: A) => {
    is_loading.value = true;
    const res = await current_api_call(a);
    if (res.success) {
      last_cursor = res.value.cursor;
      last_payload = { ...a, cursor: last_cursor };
      console.log("last_payload: ", last_payload);
      items.value = res.value.objects;
      _has_more.value = res.value.objects.length > 0 && isNotEmpty(last_cursor);
      _has_no_results.value = res.value.objects.length === 0;
    }
    is_loading.value = false;
  };

  const load_more_items = async () => {
    if (!has_more.value) {
      console.log("load_more_items: no more items to load...");
      return;
    }
    is_loading.value = true;
    const res = await current_api_call(last_payload as A);
    if (res.success) {
      last_cursor = res.value.cursor;
      last_payload = { ...(last_payload as A), cursor: last_cursor };
      items.value = [...items.value, ...res.value.objects];
      _has_more.value = res.value.objects.length > 0 && isNotEmpty(last_cursor);
    }
    is_loading.value = false;
  };

  const update_item = (by: string, item: T) => {
    const value_before = clone(
      // @ts-ignore
      items.value.find((d) => d[by] === item[by]) as T,
      true
    );
    items.value = items.value.map((l) => {
      // @ts-ignore
      if (item[by] === l[by]) {
        return {
          ...item,
        };
      }
      return l;
    });
    return function revert() {
      items.value = items.value.map((l) => {
        // @ts-ignore
        if (item[by] === l[by]) {
          return {
            ...value_before,
          };
        }
        return l;
      });
    };
  };

  const clear = () => {
    items.value = [];
    last_cursor = "";
    _has_no_results.value = false;
    _has_more.value = true;
    last_payload = undefined;
  };

  const switch_source = (
    api_call: (a: A) => Promise<ApiResult<ListResult<T>>>
  ) => {
    clear();
    current_api_call = api_call;
  };

  const mutate = (fn: (a: T[]) => T[]) => {
    items.value = fn(items.value);
  };

  const update_items_by = (by: keyof T, updated_items: T[]) => {
    const indexed_items = updated_items.reduce((acc, item) => {
      acc[item[by]] = item;
      return acc;
      // @ts-ignore
    }, {} as Record<T[K], T>);
    items.value = items.value.map((item) => {
      // @ts-ignore
      if (item[by] in indexed_items) {
        return indexed_items[item[by]];
      }
      return item;
    });
  };

  return {
    is_loading,
    items,
    clear,
    has_more,
    load_items,
    update_item,
    load_more_items,
    has_no_results,
    switch_source,
    mutate,
    update_items_by,
  };
};
